import { CUSTOM_ELEMENTS_SCHEMA, Component, NgZone } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { debounce } from 'lodash';
import { HelperProvider } from 'src/app/providers/helper/helper';
import { CommonModule } from '@angular/common';
import { MapsService } from 'src/app/providers/maps/maps.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-location-find',
  templateUrl: './location-find.modal.html',
  styleUrls: ['./location-find.modal.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LocationFindModalComponent {
  private findByLatLon = false;
  private lat: number;
  private lon: number;
  public address: string;
  public error: string;
  public predictedLocations: google.maps.places.QueryAutocompletePrediction[] = [];

  private service = new google.maps.places.PlacesService(document.createElement('div'));
  private suggestions = new google.maps.places.AutocompleteService();

  private canGetSuggestions = false;

  constructor(
    private modalController: ModalController,
    private helper: HelperProvider,
    private maps: MapsService,
    private ngZone: NgZone,
  ) { }

  private getSuggestions = debounce(() => {
    this.suggestions.getQueryPredictions(
      { input: this.address },
      this.displaySuggestions.bind(this),
    );
  }, 500);

  public async find() {
    this.error = '';
    if (this.disableSubmit()) {
      return;
    }

    if (this.findByLatLon) {
      this.maps.setCenter(this.lat, this.lon, 18);
      this.modalController.dismiss();
      return;
    }

    this.setSuggestions([]);
    this.helper.showLoading();

    this.service.findPlaceFromQuery({ query: this.address, fields: ['geometry'] }, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        const lat = results?.[0].geometry?.location?.lat();
        const lng = results?.[0].geometry?.location?.lng();

        this.helper.hideLoading();
        if (lat && lng) {
          this.maps.setCenter(lat, lng, 18);
        }
        this.modalController.dismiss();
      } else {
        this.error = 'Failed to find location';
      }

      this.helper.hideLoading();
    });
  }

  public ionInput(event: Event & {
    target: HTMLInputElement
  }) {
    this.address = event.target.value.trim();
    const latLon = this.address.split(/[\s,]+/);

    if (this.isLatLon(latLon)) {
      this.findByLatLon = true;
      this.setSuggestions([]);
      this.lat = Number(latLon[0]);
      this.lon = Number(latLon[1]);
      return;
    } else {
      this.findByLatLon = false;
    }

    if (this.canGetSuggestions && this.address) {
      this.getSuggestions();
    }

    this.canGetSuggestions = this.address.length > 4;
  }

  private displaySuggestions(
    suggestions: google.maps.places.QueryAutocompletePrediction[] | null,
    status: google.maps.places.PlacesServiceStatus,
  ) {
    if (status !== google.maps.places.PlacesServiceStatus.OK || !suggestions) {
      return;
    }

    this.setSuggestions(suggestions);
  }

  private setSuggestions(suggestions: google.maps.places.QueryAutocompletePrediction[]) {
    this.ngZone.run(() => this.predictedLocations = suggestions);
  }

  public selectPredictedLocation(location: google.maps.places.QueryAutocompletePrediction) {
    this.canGetSuggestions = false;
    this.address = location.description;
    this.setSuggestions([]);
  }

  public disableSubmit() {
    return !this.address || this.address.length < 3;
  }

  private isLatLon(latlon: string[]): boolean {
    if (latlon.length !== 2) {
      return false;
    }

    const lat = Number(latlon[0]);
    const lon = Number(latlon[1]);

    return Number.isFinite(lat)
      && Math.abs(lat) <= 90
      && Number.isFinite(lon)
      && Math.abs(lon) <= 180;
  }

}

